import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "antd";
import { motion } from "framer-motion";
import {
    FaChartLine,
} from "react-icons/fa";
import heroVisual from "../../assets/aboutus.png";
import {
    knowUsProcess,
} from "../../data/knowUsPageContent";
const trustCards = [
    {
        title: "Customer-Centric Approach",
        description: "We focus on understanding business needs before recommending solutions.",
    },
    {
        title: "Reliable Delivery",
        description: "We ensure quality execution, transparency, and timely project completion.",
    },
    {
        title: "Innovation Driven",
        description: "We leverage modern technologies, automation, and scalable architectures.",
    },
    {
        title: "Long-Term Partnership",
        description: "We provide continuous support and guidance to help businesses grow.",
    },
];

const fadeUp = {
    initial: { opacity: 0, y: 18 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: false, amount: 0.2 },
    transition: { duration: 0.5 },
};

export const KnowUsPageContent = () => {
    const navigate = useNavigate();

    return (
        <div className="know-us-page-content">
            <section className="know-us-hero">
                <div className="know-us-hero__grid">
                    <motion.div className="know-us-hero__copy" {...fadeUp}>
                        <p className="home-eyebrow">About Honesti Digital</p>
                        <h1 className="know-us-hero__title">Know Honesti Digital</h1>
                        <p className="know-us-hero__subtitle">
                            Honesti Digital helps businesses transform how they operate, engage customers, and grow
                            online. We design and deliver CRM platforms, WhatsApp automation, web and mobile
                            applications, and digital marketing systems tailored to real workflows and measurable
                            outcomes. From strategy and implementation to ongoing support, we partner with teams that
                            want to work smarter, respond faster, and scale with confidence.
                        </p>
                    </motion.div>

                    <motion.div
                        className="home-hero__visual"
                        initial={{ opacity: 0, x: 24 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: false, amount: 0.2 }}
                        transition={{ duration: 0.6 }}
                    >
                        <div className="home-hero__glow" />
                        <img
                            src={heroVisual}
                            alt="Honesti Digital team member representing our customer-focused approach"
                            className="home-hero__visual-image"
                        />
                    </motion.div>
                </div>
            </section>

            <section className="know-us-section">
                <motion.div className="know-us-section__inner know-us-section__inner--who-we-are" {...fadeUp}>
                    <h2 className="know-us-section__title">Who We Are</h2>
                    <p className="know-us-section__text">
                        Honesti Digital is a technology and business solutions company focused on helping organizations
                        streamline operations, improve customer engagement, and accelerate growth through software,
                        automation, and digital transformation services.
                    </p>
                </motion.div>
            </section>

            <section className="know-us-section know-us-section--surface">
                <div className="know-us-section__inner">
                    <div className="know-us-mv-grid">
                        <motion.article className="know-us-mv-card" {...fadeUp}>
                            <span className="know-us-mv-card__label">Mission</span>
                            <p>
                                To simplify business operations through innovative software and automation solutions.
                            </p>
                        </motion.article>
                        <motion.article className="know-us-mv-card" {...fadeUp} transition={{ duration: 0.5, delay: 0.08 }}>
                            <span className="know-us-mv-card__label">Vision</span>
                            <p>
                                To become a trusted technology partner for businesses seeking sustainable digital growth.
                            </p>
                        </motion.article>
                    </div>
                </div>
            </section>

            <section className="know-us-section know-us-section--surface">
                <div className="know-us-section__inner">
                    <div className="know-us-section__header know-us-section__header--trust">
                        <h2 className="know-us-section__title">Why Businesses Trust Honesti Digital</h2>
                        <p className="know-us-section__subtitle">
                            Building lasting partnerships through innovation, reliability, and customer success.
                        </p>
                    </div>
                    <div className="know-us-trust-list">
                        {trustCards.map((item, index) => (
                            <motion.article
                                key={item.title}
                                className="know-us-trust-item"
                                {...fadeUp}
                                transition={{ duration: 0.45, delay: index * 0.06 }}
                            >
                                <span className="know-us-trust-item__dot" aria-hidden="true" />
                                <div className="know-us-trust-item__content">
                                    <h3>{item.title}</h3>
                                    <p>{item.description}</p>
                                </div>
                            </motion.article>
                        ))}
                    </div>
                </div>
            </section>

            <section className="know-us-section know-us-process">
                <div className="know-us-section__inner">
                    <div className="know-us-section__header">
                        <h2 className="know-us-section__title">Our Process</h2>
                        <p className="know-us-section__subtitle">
                            A structured approach from discovery to sustained growth.
                        </p>
                    </div>
                    <div className="know-us-process-track">
                        {knowUsProcess.map((step, index) => (
                            <React.Fragment key={step.title}>
                                <motion.div className="know-us-process-step" {...fadeUp} transition={{ duration: 0.45, delay: index * 0.06 }}>
                                    <span className="know-us-process-step__num">{index + 1}</span>
                                    <h3>{step.title}</h3>
                                    <p>{step.description}</p>
                                </motion.div>
                                {index < knowUsProcess.length - 1 && (
                                    <div className="know-us-process-connector" aria-hidden="true">
                                        <FaChartLine />
                                    </div>
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            </section>

            <section className="know-us-cta">
                <motion.div className="know-us-cta__banner" {...fadeUp}>
                    <h2>Let&apos;s Build Something Great Together</h2>
                    <p>
                        Whether you need software, automation, or digital growth solutions, Honesti Digital is ready to
                        help.
                    </p>
                    <div className="know-us-cta__actions">
                        <Button type="primary" className="home-btn home-btn--light" onClick={() => navigate("/contact")}>
                            Contact Us
                        </Button>
                        <Button className="home-btn home-btn--outline" onClick={() => navigate("/solutions/website-development")}>
                            Explore Solutions
                        </Button>
                    </div>
                </motion.div>
            </section>
        </div>
    );
};
